// TextOverlayEditor.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Typography, Tooltip } from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";

// Types
import { TextOverlay, TextOverlayStyle } from "../types/MeGenTypes";

type TextOverlayEditorProps = {
  overlays: TextOverlay[];
  onChange: (overlays: TextOverlay[]) => void;
};

// Consts
const FONTS = ["Sarabun", "Itim", "Kanit", "Sriracha", "Charm", "Noto Sans Thai", "Playpen Sans Thai", "Montserrat"];
const ANIMATIONS = ["none", "pop-in", "fade", "slide-left", "slide-right", "slide-top", "slide-bottom"];

const ALIGNMENT_IMAGES: Record<string, string> = {
  "top-left": "static/images/megen/align_top_left.png",
  "top": "static/images/megen/align_top.png",
  "top-right": "static/images/megen/align_top_right.png",
  "left": "static/images/megen/align_left.png",
  "center": "static/images/megen/align_center.png",
  "right": "static/images/megen/align_right.png",
  "bottom-left": "static/images/megen/align_bottom_left.png",
  "bottom": "static/images/megen/align_bottom.png",
  "bottom-right": "static/images/megen/align_bottom_right.png",
};

const MAX_TEXT_LENGTH = 100;

/**========================================================================
 **                      COLOR POPOVER COMPONENT
 *========================================================================**/

type ColorPopoverProps = {
  label: string;
  color: string;
  onColorChange: (color: string) => void;
  onClose: () => void;
  onRemove?: () => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
};

const ColorPopover: React.FC<ColorPopoverProps> = (props) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  const [leftOffset, setLeftOffset] = useState<number>(0);
  useEffect(() => {
    if (props.anchorRef?.current && popoverRef.current) {
      const anchorRect = props.anchorRef.current.getBoundingClientRect();
      const popoverWidth = popoverRef.current.offsetWidth || 160;
      const parentRect = props.anchorRef.current.offsetParent?.getBoundingClientRect();
      const parentLeft = parentRect?.left ?? 0;
      const anchorOffsetLeft = anchorRect.left - parentLeft;
      setLeftOffset(anchorOffsetLeft);
    }
  }, [props.anchorRef]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        (!props.anchorRef?.current || !props.anchorRef.current.contains(e.target as Node))
      ) {
        props.onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [props.onClose, props.anchorRef]);

  return (
    <div
      ref={popoverRef}
      style={{
        position: "absolute",
        top: "calc(100% + 4px)",
        left: leftOffset,
        zIndex: 200,
        background: "white",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        minWidth: 160,
        overflow: "hidden",
      }}
    >
      {/* Color Row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "8px 12px" }}>
        <span style={{ fontSize: 11, color: "#64748b" }}>{props.label}</span>
        <input
          type="color"
          value={props.color}
          onChange={e => props.onColorChange(e.target.value)}
          style={{ width: 28, height: 28, border: "none", cursor: "pointer", borderRadius: 4, flex: 1 }}
          autoFocus
        />
      </div>

      {props.onRemove && (
        <div
          onClick={() => { props.onRemove?.(); props.onClose() }}
          style={{
            borderTop: "1px solid #f1f5f9",
            padding: "6px 12px",
            fontSize: 11,
            color: "#ef4444",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span style={{ fontSize: 13 }}>✕</span> Remove
        </div>
      )}
    </div>
  );
};

/**========================================================================
 **                     ALIGNMENT POPOVER COMPONENT
 *========================================================================**/

type AlignmentPopoverProps = {
  value: string;
  onChange: (alignment: string) => void;
  onClose: () => void;
};

const AlignmentPopover: React.FC<AlignmentPopoverProps> = (props) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) props.onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [props.onClose]);

  const grid = [
    ["top-left", "top", "top-right"],
    ["left", "center", "right"],
    ["bottom-left", "bottom", "bottom-right"],
  ];

  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        top: "calc(100% + 4px)",
        right: 0,
        zIndex: 200,
        background: "white",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        padding: 8,
      }}
    >
      {grid.map((row, ri) => (
        <div key={ri} style={{ display: "flex", gap: 4, marginBottom: ri < 2 ? 4 : 0 }}>
          {row.map(align => (
            <Tooltip key={align} title={align} placement="top">
              <button
                onClick={() => { props.onChange(align); props.onClose(); }}
                style={{
                  width: 36, height: 36,
                  border: align === props.value ? "2px solid #db2777" : "1px solid #e2e8f0",
                  borderRadius: 6,
                  background: align === props.value ? "#fdf2f8" : "white",
                  cursor: "pointer",
                  padding: 2,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <img
                  src={ALIGNMENT_IMAGES[align]}
                  alt={align}
                  style={{ width: 24, height: 24, objectFit: "contain" }}
                />
              </button>
            </Tooltip>
          ))}
        </div>
      ))}
    </div>
  );
};

const toolbarBtn = (active: boolean): React.CSSProperties => ({
  padding: "4px 7px",
  fontSize: 11,
  cursor: "pointer",
  border: "none",
  borderRadius: 6,
  backgroundColor: active ? "#e2e8f0" : "transparent",
  color: active ? "#1e293b" : "#475569",
  lineHeight: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 26,
  height: 26,
});

/**========================================================================
 **                            MAIN COMPONENT
 *========================================================================**/

const TextOverlayEditor: React.FC<TextOverlayEditorProps> = (props) => {
  // Per-overlay popover: "fontColor" | "outline" | "shadow" | "alignment" | null
  const [openPopover, setOpenPopover] = useState<Record<string, string | null>>({});

  const fontColorBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const outlineBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const shadowBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const setPopover = (id: string, type: string | null) => {
    setOpenPopover(prev => ({ ...prev, [id]: type }));
  };

  const updateOverlay = (idx: number, patch: Partial<TextOverlay>) => {
    const next = [...props.overlays];
    next[idx] = { ...next[idx], ...patch };
    props.onChange(next);
  };

  const updateStyle = (idx: number, patch: Partial<TextOverlayStyle>) => {
    updateOverlay(idx, { style: { ...props.overlays[idx].style, ...patch } });
  };

  const updateStyleDebounced = useCallback(
    (idx: number, patch: Partial<TextOverlayStyle>, overlaysSnapshot: TextOverlay[], debounceMs = 400) => {
      const key = `style-${idx}-${Object.keys(patch).join(",")}`;
      if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
      debounceTimers.current[key] = setTimeout(() => {
        const next = [...overlaysSnapshot];
        next[idx] = { ...next[idx], style: { ...next[idx].style, ...patch } };
        props.onChange(next);
        delete debounceTimers.current[key];
      }, debounceMs);
    },
    [props.onChange]
  );

  const updateOverlayDebounced = useCallback(
    (idx: number, patch: Partial<TextOverlay>, overlaysSnapshot: TextOverlay[], debounceMs = 400) => {
      const key = `overlay-${idx}-${Object.keys(patch).join(",")}`;
      if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
      debounceTimers.current[key] = setTimeout(() => {
        const next = [...overlaysSnapshot];
        next[idx] = { ...next[idx], ...patch };
        props.onChange(next);
        delete debounceTimers.current[key];
      }, debounceMs);
    },
    [props.onChange]
  );

  // Local state สำหรับ display เท่านั้น (ไม่รอ debounce)
  // เก็บ overlays ที่ user เห็น realtime ใน localOverlays
  const [localOverlays, setLocalOverlays] = useState<TextOverlay[]>(props.overlays);
  useEffect(() => { setLocalOverlays(props.overlays); }, [props.overlays]);

  const addOverlay = () => {
    const newOverlay: TextOverlay = {
      id: Date.now().toString(),
      text: "",
      style: { fontSize: 64, color: "white", font: "Sarabun" },
    };
    const next = [...props.overlays, newOverlay];
    setLocalOverlays(next);
    props.onChange(next);
  };

  const removeOverlay = (idx: number) => {
    const next = localOverlays.filter((_, i) => i !== idx);
    setLocalOverlays(next);
    props.onChange(next);
  };

  const handleStyleChange = (idx: number, patch: Partial<TextOverlayStyle>) => {
    const next = [...localOverlays];
    next[idx] = { ...next[idx], style: { ...next[idx].style, ...patch } };
    setLocalOverlays(next);
    updateStyleDebounced(idx, patch, localOverlays);
  };

  const handleOverlayChange = (idx: number, patch: Partial<TextOverlay>) => {
    const next = [...localOverlays];
    next[idx] = { ...next[idx], ...patch };
    setLocalOverlays(next);
    updateOverlayDebounced(idx, patch, localOverlays);
  };

  const handleStyleImmediate = (idx: number, patch: Partial<TextOverlayStyle>) => {
    const next = [...localOverlays];
    next[idx] = { ...next[idx], style: { ...next[idx].style, ...patch } };
    setLocalOverlays(next);
    props.onChange(next);
  };

  const handleOverlayImmediate = (idx: number, patch: Partial<TextOverlay>) => {
    const next = [...localOverlays];
    next[idx] = { ...next[idx], ...patch };
    setLocalOverlays(next);
    props.onChange(next);
  };

  return (
    <Box id="megen-text-overlay-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Charm&family=Itim&family=Kanit&family=Noto+Sans+Thai&family=Playpen+Sans+Thai&family=Sarabun&family=Sriracha&display=swap');
        .tov-toolbar-btn { transition: background 0.15s; }
        .tov-toolbar-btn:hover { background: #f1f5f9 !important; }
      `}</style>

      {localOverlays.map((overlay, idx) => {
        const pop = openPopover[overlay.id] ?? null;
        const textLen = overlay.text?.length ?? 0;
        const duration =
          overlay.end !== undefined && overlay.start !== undefined
            ? overlay.end - overlay.start
            : undefined;

        const fontColorAnchorRef = { current: fontColorBtnRefs.current[overlay.id + "_fc"] } as React.RefObject<HTMLElement>;
        const outlineAnchorRef = { current: outlineBtnRefs.current[overlay.id + "_ol"] } as React.RefObject<HTMLElement>;
        const shadowAnchorRef = { current: shadowBtnRefs.current[overlay.id + "_sh"] } as React.RefObject<HTMLElement>;

        return (
          <Box
            id={`megen-text-overlay-item-${overlay.id}`}
            key={overlay.id}
            sx={{
              border: "1px solid #e8e8e8",
              borderRadius: 3,
              p: 2,
              pt: 1,
              mb: 2,
              backgroundColor: "white",
              position: "relative",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            }}
          >
            {/* Delete */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <button
                id={`megen-text-overlay-remove-btn-${overlay.id}`}
                onClick={() => removeOverlay(idx)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: "#ef4444", padding: 4, borderRadius: 6,
                  display: "flex", alignItems: "center",
                }}
              >
                <DeleteOutline sx={{ fontSize: 20 }} />
              </button>
            </Box>

            {/* ── Style Toolbar (Row 1 + Row 2 in same Box) ── */}
            <Box
              id={`megen-text-overlay-toolbar-${overlay.id}`}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mb: 1.5,
                p: "8px 10px",
                background: "#f8fafc",
                borderRadius: 2,
                border: "1px solid #e2e8f0",
              }}
            >
              {/* ── Row 1 ── */}
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}>

                {/* Font Selector */}
                <select
                  style={{
                    fontSize: 11,
                    border: "1px solid #e2e8f0",
                    borderRadius: 6,
                    padding: "3px 6px",
                    backgroundColor: "white",
                    color: "#1e293b",
                    cursor: "pointer",
                    height: 26,
                    fontFamily: overlay.style.font || "Sarabun",
                    width: 136,
                  }}
                  value={overlay.style.font || "Sarabun"}
                  onChange={e => handleStyleImmediate(idx, { font: e.target.value })}
                >
                  {FONTS.map(f => (
                    <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                  ))}
                </select>

                {/* Font Size: − / number / + */}
                <Box sx={{ display: "flex", alignItems: "center", border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden", background: "white" }}>
                  <button
                    className="tov-toolbar-btn"
                    style={{ ...toolbarBtn(false), borderRadius: 0 }}
                    onClick={() => handleStyleChange(idx, { fontSize: Math.max(8, (overlay.style.fontSize || 64) - 2) })}
                  >-</button>
                  <input
                    type="number"
                    value={overlay.style.fontSize || 64}
                    onChange={e => handleStyleChange(idx, { fontSize: parseInt(e.target.value) || 64 })}
                    style={{
                      width: 34, textAlign: "center", fontSize: 11,
                      border: "none", borderLeft: "1px solid #e2e8f0", borderRight: "1px solid #e2e8f0",
                      outline: "none", padding: "2px 0", height: 26,
                    }}
                  />
                  <button
                    className="tov-toolbar-btn"
                    style={{ ...toolbarBtn(false), borderRadius: 0 }}
                    onClick={() => handleStyleChange(idx, { fontSize: (overlay.style.fontSize || 64) + 2 })}
                  >+</button>
                </Box>

                <div style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 2px" }} />

                {/* Bold */}
                <button
                  className="tov-toolbar-btn"
                  style={{ ...toolbarBtn(!!overlay.style.bold), fontWeight: "bold" }}
                  onClick={() => handleStyleImmediate(idx, { bold: !overlay.style.bold })}
                >B</button>

                {/* Italic */}
                <button
                  className="tov-toolbar-btn"
                  style={{ ...toolbarBtn(!!overlay.style.italic), fontStyle: "italic" }}
                  onClick={() => handleStyleImmediate(idx, { italic: !overlay.style.italic })}
                >I</button>

                {/* Underline */}
                <button
                  className="tov-toolbar-btn"
                  style={{ ...toolbarBtn(!!overlay.style.underline), textDecoration: "underline" }}
                  onClick={() => handleStyleImmediate(idx, { underline: !overlay.style.underline })}
                >U</button>

                <Box sx={{ position: "relative" }}>
                  <Tooltip title="Font Color">
                    <button
                      id={`megen-text-overlay-font-color-btn-${overlay.id}`}
                      ref={el => { fontColorBtnRefs.current[overlay.id + "_fc"] = el; }}
                      className="tov-toolbar-btn"
                      style={{ ...toolbarBtn(pop === "fontColor"), padding: "2px 5px" }}
                      onClick={() => setPopover(overlay.id, pop === "fontColor" ? null : "fontColor")}
                    >
                      <svg width="16" height="16" viewBox="0 0 18 18">
                        <text x="4" y="13" fontSize="13" fontWeight="bold" fill="currentColor" fontFamily="sans-serif">A</text>
                        <rect x="1" y="14.5" width="16" height="2.5" rx="1" fill={overlay.style.color || "white"} stroke="#cbd5e1" strokeWidth="0.5" />
                      </svg>
                    </button>
                  </Tooltip>
                  {pop === "fontColor" && (
                    <ColorPopover
                      label="Fonts Color"
                      color={overlay.style.color || "white"}
                      anchorRef={fontColorAnchorRef}
                      onColorChange={c => handleStyleChange(idx, { color: c })}
                      onClose={() => setPopover(overlay.id, null)}
                    />
                  )}
                </Box>

                <div style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 2px" }} />

                <Box sx={{ position: "relative" }}>
                  <Tooltip title="Outline Color">
                    <button
                      id={`megen-text-overlay-outline-btn-${overlay.id}`}
                      ref={el => { outlineBtnRefs.current[overlay.id + "_ol"] = el; }}
                      className="tov-toolbar-btn"
                      style={{ ...toolbarBtn(!!overlay.style.outlineColor) }}
                      onClick={() => {
                        if (!overlay.style.outlineColor) {
                          handleStyleImmediate(idx, { outlineColor: "black" });
                          setPopover(overlay.id, "outline");
                        } else {
                          setPopover(overlay.id, pop === "outline" ? null : "outline");
                        }
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 18 18">
                        <text x="4" y="14" fontSize="14" fontWeight="bold" fill="none" stroke="currentColor" strokeWidth="1.5" fontFamily="sans-serif">T</text>
                      </svg>
                    </button>
                  </Tooltip>
                  {overlay.style.outlineColor && (
                    <div style={{
                      position: "absolute", bottom: -2, right: -2,
                      width: 8, height: 8, borderRadius: "50%",
                      background: overlay.style.outlineColor,
                      border: "1px solid #fff",
                      pointerEvents: "none",
                    }} />
                  )}
                  {pop === "outline" && (
                    <ColorPopover
                      label="Outline"
                      color={overlay.style.outlineColor || "black"}
                      anchorRef={outlineAnchorRef}
                      onColorChange={c => handleStyleChange(idx, { outlineColor: c })}
                      onRemove={() => {
                        handleStyleImmediate(idx, { outlineColor: undefined });
                        setPopover(overlay.id, null);
                      }}
                      onClose={() => setPopover(overlay.id, null)}
                    />
                  )}
                </Box>

                <Box sx={{ position: "relative" }}>
                  <Tooltip title="Text Shadow">
                    <button
                      id={`megen-text-overlay-shadow-btn-${overlay.id}`}
                      ref={el => { shadowBtnRefs.current[overlay.id + "_sh"] = el; }}
                      className="tov-toolbar-btn"
                      style={{ ...toolbarBtn(!!overlay.style.shadowColor) }}
                      onClick={() => {
                        if (!overlay.style.shadowColor) {
                          handleStyleImmediate(idx, { shadowColor: "black" });
                          setPopover(overlay.id, "shadow");
                        } else {
                          setPopover(overlay.id, pop === "shadow" ? null : "shadow");
                        }
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 18 18">
                        <defs>
                          <filter id={`ds-${overlay.id}`} x="-20%" y="-20%" width="160%" height="160%">
                            <feDropShadow dx="1.2" dy="1.2" stdDeviation="0.8" floodColor="#888" floodOpacity="0.9" />
                          </filter>
                        </defs>
                        <text x="4" y="14" fontSize="14" fontWeight="bold" fill="currentColor" fontFamily="sans-serif" filter={`url(#ds-${overlay.id})`}>T</text>
                      </svg>
                    </button>
                  </Tooltip>
                  {overlay.style.shadowColor && (
                    <div style={{
                      position: "absolute", bottom: -2, right: -2,
                      width: 8, height: 8, borderRadius: "50%",
                      background: overlay.style.shadowColor,
                      border: "1px solid #fff",
                      pointerEvents: "none",
                    }} />
                  )}
                  {pop === "shadow" && (
                    <ColorPopover
                      label="Shadow"
                      color={overlay.style.shadowColor || "black"}
                      anchorRef={shadowAnchorRef}
                      onColorChange={c => handleStyleChange(idx, { shadowColor: c })}
                      onRemove={() => {
                        handleStyleImmediate(idx, { shadowColor: undefined });
                        setPopover(overlay.id, null);
                      }}
                      onClose={() => setPopover(overlay.id, null)}
                    />
                  )}
                </Box>

                {/* Text Alignment */}
                <Box sx={{ position: "relative" }}>
                  <Tooltip title="Text Alignment">
                    <button
                      id={`megen-text-overlay-align-btn-${overlay.id}`}
                      className="tov-toolbar-btn"
                      style={{ ...toolbarBtn(pop === "alignment"), padding: "3px 5px" }}
                      onClick={() => setPopover(overlay.id, pop === "alignment" ? null : "alignment")}
                    >
                      <img
                        src={ALIGNMENT_IMAGES[overlay.style.alignment || "bottom"]}
                        alt={overlay.style.alignment || "bottom"}
                        style={{ width: 16, height: 16, objectFit: "contain" }}
                      />
                      <svg width="7" height="7" viewBox="0 0 8 8" style={{ marginLeft: 2 }}>
                        <path d="M0 2 L4 6 L8 2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </button>
                  </Tooltip>
                  {pop === "alignment" && (
                    <AlignmentPopover
                      value={overlay.style.alignment || "bottom"}
                      onChange={a => handleStyleImmediate(idx, { alignment: a })}
                      onClose={() => setPopover(overlay.id, null)}
                    />
                  )}
                </Box>
              </Box>

              {/* ── Row 2: Animation & Timing ── */}
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, pt: 0.75, borderTop: "1px solid #e2e8f0" }}>
                {/* Animation */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography sx={{ fontSize: "0.68rem", color: "#64748b", whiteSpace: "nowrap" }}>Animation</Typography>
                  <select
                    style={{
                      fontSize: 11,
                      border: "1px solid #e2e8f0",
                      borderRadius: 6,
                      padding: "3px 6px",
                      backgroundColor: "white",
                      color: "#1e293b",
                      cursor: "pointer",
                      height: 26,
                    }}
                    value={overlay.style.animation || "none"}
                    onChange={e => handleStyleImmediate(idx, { animation: e.target.value as any })}
                  >
                    {ANIMATIONS.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </Box>

                <div style={{ width: 1, height: 18, background: "#e2e8f0" }} />

                {/* Start(s) */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography sx={{ fontSize: "0.68rem", color: "#64748b", whiteSpace: "nowrap" }}>Start (s)</Typography>
                  <input
                    type="number" step={0.1} min={0}
                    value={overlay.start ?? 0}
                    style={{
                      width: 48, fontSize: 11,
                      border: "1px solid #e2e8f0", borderRadius: 6,
                      padding: "3px 6px", height: 26, textAlign: "center",
                    }}
                    onChange={e => handleOverlayImmediate(idx, { start: parseFloat(e.target.value) || 0 })}
                  />
                </Box>

                {/* Dur(s) */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography sx={{ fontSize: "0.68rem", color: "#64748b", whiteSpace: "nowrap" }}>Duration (s)</Typography>
                  <input
                    type="number" step={0.1} min={0} placeholder="All"
                    value={duration !== undefined ? duration.toFixed(1) : ""}
                    style={{
                      width: 48, fontSize: 11,
                      border: "1px solid #e2e8f0", borderRadius: 6,
                      padding: "3px 6px", height: 26, textAlign: "center",
                    }}
                    onChange={e => {
                      const dur = parseFloat(e.target.value);
                      const start = overlay.start || 0;
                      handleOverlayImmediate(idx, { end: isNaN(dur) ? undefined : start + dur });
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* ── Text Input ── */}
            <Box sx={{ position: "relative", mb: 1.5 }}>
              <textarea
                id={`megen-text-overlay-input-${overlay.id}`}
                rows={2}
                maxLength={MAX_TEXT_LENGTH}
                style={{
                  width: "100%",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontSize: 13,
                  outline: "none",
                  resize: "vertical",
                  boxSizing: "border-box",
                  color: "#1e293b",
                  lineHeight: 1.5,
                }}
                value={overlay.text}
                placeholder="ระบุข้อความ จำกัดไม่เกิน 100 ตัวอักษร..."
                onChange={e => {
                  if (e.target.value.length <= MAX_TEXT_LENGTH) {
                    handleOverlayChange(idx, { text: e.target.value });
                  }
                }}
              />
              <Typography
                sx={{
                  position: "absolute", bottom: 8, right: 10,
                  fontSize: "0.62rem",
                  color: textLen >= MAX_TEXT_LENGTH ? "#ef4444" : "#94a3b8",
                  pointerEvents: "none",
                }}
              >
                {textLen}/{MAX_TEXT_LENGTH}
              </Typography>
            </Box>

            {/* ── Preview ── */}
            <Box>
              <Typography sx={{ fontSize: "0.72rem", color: "#64748b", mb: 0.5, fontWeight: 500 }}>
                Preview Text & Style
              </Typography>
              <Box
                sx={{
                  backgroundColor: "#e8e8e8",
                  borderRadius: 2,
                  minHeight: 90,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  px: 2, py: 1.5,
                }}
              >
                <p style={{
                  fontFamily: overlay.style.font || "Sarabun",
                  fontSize: Math.max(12, (overlay.style.fontSize || 64) / 4),
                  color: overlay.style.color || "black",
                  fontWeight: overlay.style.bold ? "bold" : "normal",
                  fontStyle: overlay.style.italic ? "italic" : "normal",
                  textDecoration: overlay.style.underline ? "underline" : "none",
                  WebkitTextStroke: overlay.style.outlineColor
                    ? `${Math.max(0.5, (overlay.style.fontSize || 64) / 120)}px ${overlay.style.outlineColor}`
                    : "0",
                  paintOrder: "stroke fill",
                  textShadow: overlay.style.shadowColor ? `2px 2px 4px ${overlay.style.shadowColor}` : "none",
                  textAlign: "center",
                  margin: 0,
                  wordBreak: "break-word",
                  maxWidth: "100%",
                }}>
                  {overlay.text || "ระบุข้อความ จำกัดไม่เกิน 100 ตัวอักษร..."}
                </p>
              </Box>
            </Box>
          </Box>
        );
      })}

      <Button
        id="megen-text-overlay-add-btn"
        fullWidth
        variant="outlined"
        size="small"
        onClick={addOverlay}
        sx={{
          borderColor: "#f9a8d4",
          color: "#db2777",
          fontSize: "0.72rem",
          "&:hover": { backgroundColor: "#fdf2f8", borderColor: "#be185d" },
        }}
      >
        + Add Text Overlay
      </Button>
    </Box>
  );
};

export default TextOverlayEditor;